import React from 'react';
import styled from "@emotion/styled";
import { Inner } from 'styles/structure';
import colors from "styles/colors";
import dimensions from 'styles/dimensions';
import Checkmark from '../../../../vectors/checkmark.svg';
import ReactTooltip from 'react-tooltip';

const FeatureComparisonContainer = styled.div`
  padding-bottom: 8rem;
  
  .tooltip {
    background-color: #fff;
    color: ${colors.teal800};
    font-size: 1.4rem;
    line-height: 1.44;
    vertical-align: middle;
    border: 1px solid ${colors.grey200};
    box-shadow: 0 0.3rem 0.4rem rgba(0,0,0,0.08);
    padding: 1.6rem 2.4rem 1.2rem;
    max-width: 32rem;
    
    &::after {
      display: none;
    }
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    padding-bottom: 14.4rem;
  }
`;

const ComparisonTable = styled.table`
  text-align: center;
  margin: auto;
  position: relative;
  width: 100%;
  font-size: 1.4rem;
`;

const ComparisonRow = styled.tr`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  background-color: #fff;
  width: 100%;

  &:first-of-type {
    box-shadow: 0 0.4rem 1rem -1rem rgba(0,0,0,0.4);
  
    th {
      flex: 1 1 auto;
      display: flex;
      justify-content: center;
      width: 25%;
      padding-top: 0;
      padding-bottom: 1.6rem;
        
      &:first-of-type {
        flex: 1 0 100%;
        padding-top: 2.4rem;
        font-size: 1.8rem;
      }
    }
  }
  
  &.ComparisonRow--header {
    &:first-of-type {
      position: sticky;
      top: 4.8rem;
      z-index: 10; 
      
      &::before,
      &::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        width: 1.8rem;
        height: 100%;
        background-color: #fff;
      }
      
      &::before {
        right: 100%;
      }
      
      &::after {
        left: 100%;
      }
    }
  }
  
  &.ComparisonRow--sub {
    &:nth-of-type(2n) {
      position: relative;
      background-color: ${colors.orange100};
      
      &::before,
      &::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        width: 1.8rem;
        height: 100%;
        background-color: ${colors.orange100};
      }
      
      &::before {
        right: 100%;
      }
      
      &::after {
        left: 100%;
      }
    }
    
    svg {
      position: relative;
      top: 0.2rem;
      width: 1.6rem;
      height: 1.2rem;
      fill: ${colors.teal800};
    }
  }
  
  &::before,
  &::after {
    display: table;
    content: '';
  }
  
  td {
    color: ${colors.teal800}; 
    flex: 1 1 auto;
    width: 25%;
    padding: 1.6rem 0.4rem;
    
    &:first-of-type {
      flex: 1 0 100%;
      padding-bottom: 0;
    }
    
    &.has-tooltip {      
      span {
        cursor: pointer;
        padding-bottom: 0.2rem;
        background-image: repeating-linear-gradient(to right, ${colors.red500}, ${colors.red500} 0.4rem, #fff 0.4rem, #fff 0.7rem);
        background-position: 0% 100%;
        background-repeat: no-repeat;
        background-size: 100% 1px;
        transition: background 0.16s ease-in-out;
        
        &:hover {
          background-image: repeating-linear-gradient(to right, ${colors.green400}, ${colors.green400} 0.4rem, #fff 0.4rem, #fff 0.7rem);
        }
      }
    }
  }
  
  th {
    color: ${colors.teal800};
    font-size: 1.4rem;
    font-weight: 600;
    padding: 4.8rem 0.4rem;
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    &.ComparisonRow--header:first-of-type {
      top: 8.8rem;
    }
    
    &.ComparisonRow--header:first-of-type,
    &.ComparisonRow--sub:nth-of-type(2n) {
      &::before,
      &::after {
        width: 3.2rem;
      }
    }
  }
  
  @media(min-width: ${dimensions.desktopUp}px) {
    justify-content: flex-start;
    
    &.ComparisonRow--header:first-of-type {        
      th {          
        width: 20%;
        padding-top: 2.2rem;

        &:first-of-type {
          display: block;
          text-align: left;
          flex: 1 0 20%;
          padding-top: 2.2rem;
        }
      }
    }    
    
    th {
      font-size: 1.8rem;
      text-align: left;
      padding-top: 7.2rem;
      padding-bottom: 1.6rem;
    }
    
    td {
      width: 20%;
      padding: 2.2rem 0.8rem;
      
      &:first-of-type {
        flex: 1 1 auto;
        text-align: left;
        padding-bottom: 2.2rem;
      }
    }
    
    &.ComparisonRow--sub {
      svg {
        width: 2rem;
        height: 1.6rem;
      }
    }
  }
`;

const FeatureComparison = ({ featureGroups, plans, products }) => {
  return (
    <FeatureComparisonContainer>
      <Inner>
        <ComparisonTable>
          <tbody>
            {/* Sticky header row */}
            <ComparisonRow className='ComparisonRow--header'>
              <th><span>Features</span></th>

              {plans && plans.slice(0,4).map(({plan}, i) => (
                <th key={i}><span>{plan.display_title}</span></th>
              ))}

              {products && products.slice(0,4).map((product, i) => (
                <th key={i}><span>{product.product.name}</span></th>
              ))}
            </ComparisonRow>

            {featureGroups.map((group, i) => (
              <React.Fragment key={i}>
                <ComparisonRow className='ComparisonRow--header'>
                  <th><span>{group.primary.heading}</span></th>
                </ComparisonRow>

                {group.fields.map(({feature, text}, i) => (
                  <ComparisonRow
                    className='ComparisonRow--sub'
                    key={i}
                  >
                    <td className={feature.tooltip && 'has-tooltip'}><span data-for="info" data-tip={feature.tooltip}>{feature.name}</span></td>

                    {plans && plans.slice(0,4).map(({plan}, i) => {
                      let allFeatures = [];
                      let featureText = false;

                      if (plan.features.length) {
                        allFeatures = plan.features.map((item) => {
                          if (item.text && item.feature.name === feature.name) {
                            featureText = item.text;
                          }

                          return item.feature && item.feature.name;
                        });
                      }

                      if (featureText) {
                        return (
                          <td key={i}>
                            <span>{featureText}</span>
                          </td>
                        );
                      } else {
                        return (
                          <td key={i}>
                            <span>{allFeatures.includes(feature.name) && <Checkmark/>}</span>
                          </td>
                        );
                      }

                    })}

                    {products && products.slice(0,4).map(({product}, i) => {
                      let allFeatures = [];

                      if (product.comparison_features.length) {
                        allFeatures = product.comparison_features.map(({feature}) => {
                          return feature && feature.name;
                        });
                      }

                      return <td key={i}><span>{allFeatures.includes(feature.name) && <Checkmark/>}</span></td>;
                    })}
                  </ComparisonRow>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </ComparisonTable>

        <ReactTooltip className='tooltip' id='info' place='right'/>
      </Inner>
    </FeatureComparisonContainer>
  );
};

export default FeatureComparison;
